import { useState, useCallback, useEffect } from 'react'
import { usePlayground } from '../store'
import { useTheme } from '../contexts/ThemeContext'
import { renderSvg as renderSvgViaCore, renderPng as renderPngViaServer } from '../api/enApi'
import { CanvasSettings, type StructureFilters } from './CanvasSettings'
import { ThemeSheet } from './ThemeSheet'
import { CanvasOverlay } from './CanvasOverlay'
import { MobileSheet } from './MobileSheet'
import { ToolbarBtn, ProcessingDot, Icons } from './TabToolbar'
import { THEME_GROUPS } from '../lib/themeMap'

// Names produced by the seed-derived path (Material 3 / fallback themes).
// Anything OTHER than these is a curated theme — pass its display name to the
// renderer instead of the seed. Mirrors the logic in Playground.tsx so analyze()
// and renderSvg() pick the same theme parameters.
const SEED_DERIVED_NAMES = new Set(['Indigo', 'Paper', 'Dark', 'Light', 'Random', 'Custom'])

/**
 * Convert the picker's StructureFilters object into the bitmask that
 * SvgRenderer / RenderApi.renderSvg expects. Bit positions match
 * core/render/StructureLayers.kt:
 *   1 = subsystems, 2 = pipelines, 4 = cycles, 8 = forks, 16 = joins,
 *   32 = hubs, 64 = deadlock, 128 = overflow
 * All on = 255. All off = 0.
 */
function structureFiltersToBitmask(f: StructureFilters): number {
    return (f.subsystems ? 1 : 0)
        | (f.pipelines ? 2 : 0)
        | (f.cycles ? 4 : 0)
        | (f.forks ? 8 : 0)
        | (f.joins ? 16 : 0)
        | (f.hubs ? 32 : 0)
        | (f.deadlock ? 64 : 0)
        | (f.overflow ? 128 : 0)
}

// Vite resolves these to served URLs at build time. Used to embed JetBrains Mono
// as a base64 @font-face inside exported SVGs so the file renders identically
// when opened standalone (without the playground's page-level font loading).
import jbm400Url from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-400-normal.woff2?url'
import jbm500Url from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-500-normal.woff2?url'
import jbm700Url from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-700-normal.woff2?url'

// Module-level cache: fetch + base64-encode the fonts once per page load. Subsequent
// exports skip the fetch entirely.
let _embeddedFontCss: Promise<string> | null = null
async function getEmbeddedFontCss(): Promise<string> {
    if (_embeddedFontCss) return _embeddedFontCss
    _embeddedFontCss = (async () => {
        const fetchAsBase64 = async (url: string): Promise<string> => {
            const response = await fetch(url)
            if (!response.ok) throw new Error(`Font fetch failed: ${url} (${response.status})`)
            const buffer = await response.arrayBuffer()
            // Chunked encoding to avoid call-stack overflow on large binaries.
            const bytes = new Uint8Array(buffer)
            let binary = ''
            const chunkSize = 0x8000
            for (let i = 0; i < bytes.length; i += chunkSize) {
                binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)))
            }
            return btoa(binary)
        }
        try {
            const [b400, b500, b700] = await Promise.all([
                fetchAsBase64(jbm400Url),
                fetchAsBase64(jbm500Url),
                fetchAsBase64(jbm700Url),
            ])
            return `
@font-face { font-family: 'JetBrains Mono'; font-style: normal; font-weight: 400; src: url(data:font/woff2;base64,${b400}) format('woff2'); }
@font-face { font-family: 'JetBrains Mono'; font-style: normal; font-weight: 500; src: url(data:font/woff2;base64,${b500}) format('woff2'); }
@font-face { font-family: 'JetBrains Mono'; font-style: normal; font-weight: 700; src: url(data:font/woff2;base64,${b700}) format('woff2'); }
`
        } catch (e) {
            console.warn('[Export] Font embed failed, exported SVG may fall back to system fonts:', e)
            return ''
        }
    })()
    return _embeddedFontCss
}

// All layers on by default except `subsystems` (the top-level structural
// community grouping) — users can still toggle it from the layers menu.
const FILTER_DEFAULTS: StructureFilters = { subsystems: false, pipelines: true, cycles: true, forks: true, joins: true, hubs: true, deadlock: true, overflow: true }

interface Props {
    viewportRef: React.RefObject<HTMLDivElement | null>
    structureFilters: StructureFilters
    onFiltersChange: (f: StructureFilters) => void
}

export function CanvasToolbar({ viewportRef, structureFilters, onFiltersChange }: Props) {
    const scene = usePlayground((s) => s.scene)
    const source = usePlayground((s) => s.source)
    const groupBy = usePlayground((s) => s.groupBy)
    const transform = usePlayground((s) => s.transform)
    const setTransform = usePlayground((s) => s.setTransform)
    const focusedNodeId = usePlayground((s) => s.focusedNodeId)
    const projectName = usePlayground((s) => s.projectName)
    const isProcessing = usePlayground((s) => s.isProcessing)
    const isMobile = usePlayground((s) => s.isMobile)
    const leftOpen = usePlayground((s) => s.leftOpen)
    const rightOpen = usePlayground((s) => s.rightOpen)
    const setLeftOpen = usePlayground((s) => s.setLeftOpen)
    const setRightOpen = usePlayground((s) => s.setRightOpen)

    const pendingFocusNodeId = usePlayground((s) => s.pendingFocusNodeId)
    const pendingFocusSeq = usePlayground((s) => s.pendingFocusSeq)
    const clearPendingFocus = usePlayground((s) => s.clearPendingFocus)

    const { theme, themeName, setTheme, setThemeFromSeed } = useTheme()
    const isDark = theme.isDark ?? themeName === 'dark'

    // Current theme group (match by key, then by display name as bootstrap fallback).
    const activeGroup = THEME_GROUPS.find(g => g.light?.key === themeName || g.dark?.key === themeName)
        ?? THEME_GROUPS.find(g => g.name === theme.name)
    const themeNameLabel = activeGroup?.name.toLowerCase()
        ?? (theme.seedColor != null ? 'material' : 'default')
    const modeValueLabel = isDark ? 'dark' : 'light'

    // Toggle dark/light while preserving the current theme identity.
    //   • Curated group: swap to the matching variant in the same group.
    //   • Material (seed-derived, including the bootstrap default):
    //     regenerate via HCT with the same seed at the new mode.
    const toggleMode = () => {
        const DEFAULT_SEED = '#6366f1'
        const newIsDark = !isDark
        if (activeGroup) {
            const otherVariant = newIsDark ? activeGroup.dark : activeGroup.light
            if (otherVariant) { setTheme(otherVariant.key); return }
            setThemeFromSeed(
                activeGroup.light?.theme.seedColor ?? activeGroup.dark?.theme.seedColor ?? DEFAULT_SEED,
                newIsDark,
            )
            return
        }
        setThemeFromSeed(theme.seedColor ?? DEFAULT_SEED, newIsDark)
    }

    const exportFilename = (ext: 'svg' | 'png') => {
        const slug = (projectName || 'diagram').replace(/\s+/g, '-').toLowerCase()
        const mode = isDark ? 'dark' : 'light'
        return `${slug}-${mode}.${ext}`
    }

    const [settingsOpen, setSettingsOpen] = useState(false)
    const [themeOpen, setThemeOpen] = useState(false)
    const [isFullscreen, setIsFullscreen] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [downloadOpen, setDownloadOpen] = useState(false)

    // Fullscreen tracking
    useEffect(() => {
        const fn = () => setIsFullscreen(!!document.fullscreenElement)
        document.addEventListener('fullscreenchange', fn)
        return () => document.removeEventListener('fullscreenchange', fn)
    }, [])

    // Close settings on outside click
    useEffect(() => {
        if (!settingsOpen) return
        const fn = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (!target.closest('[data-settings-panel]') && !target.closest('[data-settings-trigger]')) {
                setSettingsOpen(false)
            }
        }
        document.addEventListener('mousedown', fn)
        return () => document.removeEventListener('mousedown', fn)
    }, [settingsOpen])

    // Close theme sheet on outside click
    useEffect(() => {
        if (!themeOpen) return
        const fn = (e: MouseEvent) => {
            const target = e.target as HTMLElement
            if (!target.closest('[data-theme-panel]') && !target.closest('[data-theme-trigger]')) {
                setThemeOpen(false)
            }
        }
        document.addEventListener('mousedown', fn)
        return () => document.removeEventListener('mousedown', fn)
    }, [themeOpen])

    const zoomPercent = Math.round(transform.scale * 100)

    const handleZoomIn = useCallback(() => setTransform(t => ({ ...t, scale: Math.min(t.scale * 1.2, 10) })), [])
    const handleZoomOut = useCallback(() => setTransform(t => ({ ...t, scale: Math.max(t.scale / 1.2, 0.1) })), [])

    const handleFit = useCallback(() => {
        if (!scene || !viewportRef.current) return
        const r = viewportRef.current.getBoundingClientRect()
        const p = 40
        const s = Math.min((r.width - p * 2) / scene.width, (r.height - p * 2) / scene.height, 1.5)
        setTransform({ x: (r.width - scene.width * s) / 2, y: (r.height - scene.height * s) / 2, scale: s })
    }, [scene])

    const handleFocusSelection = useCallback(() => {
        if (!focusedNodeId || !scene || !viewportRef.current) return
        const node = scene.nodes.find(n => n.id === focusedNodeId)
        if (!node) return
        const r = viewportRef.current.getBoundingClientRect()
        const s = Math.max(transform.scale, 0.8)
        setTransform({ x: r.width / 2 - node.x * s, y: r.height / 2 - node.y * s, scale: s })
    }, [focusedNodeId, scene, transform.scale])

    const handleFullscreen = useCallback(() => {
        const el = viewportRef.current
        if (!el) return
        if (document.fullscreenElement) document.exitFullscreen()
        else el.requestFullscreen()
    }, [])

    /**
     * Render the diagram via core/render's SvgRenderer (the single source of truth
     * for rendering). In dev mode this goes through the KMP JS bundle directly via
     * EndiagramJsApi.renderSvg; in prod mode it goes through the MCP /render tool.
     * Either way the output is pure native SVG — no foreignObject, no React canvas.
     *
     * The SVG that comes back already has font-family declared on text elements
     * via SvgRenderer's <style> block, but it references 'JetBrains Mono' by name
     * with a Google Fonts @import. For offline portability we splice in @font-face
     * data URIs so the file renders correctly in any viewer (Preview, Inkscape, etc.)
     * without needing internet.
     */
    const renderExportSvg = useCallback(async (): Promise<{ svg: string; width: number; height: number } | null> => {
        if (!source || !scene) return null
        // Theme inputs that compose the export render request:
        //   - Curated theme (Editorial): pass theme.name as the preset name;
        //     renderer's ThemeEngine.byName resolves the right variant via isDark.
        //   - Material with custom seed: pass theme.seedColor; renderer regenerates
        //     via HCT.
        //   - Default (Editorial dark / Editorial light): both undefined, renderer uses
        //     themeSet defaults from isDark.
        const isCurated = !SEED_DERIVED_NAMES.has(theme.name)
        const presetName = isCurated ? theme.name : undefined
        const seedColor = isCurated ? undefined : (theme.seedColor ?? undefined)
        // groupBy ('actor' | 'location' | undefined) → renderer's view ('actors' |
        // 'locations' | undefined). Same conversion Playground.tsx uses for analyze().
        // The renderer uses this to partition the layout by actor/location boundaries.
        const view = groupBy === 'actor' ? 'actors' : groupBy === 'location' ? 'locations' : undefined
        // Structure-layers bitmask from the picker's current toggle state. Default
        // (all off) → 0, all on → 63. Renderer uses this to filter which structural
        // overlays (subsystems, pipelines, cycles, forks, joins, hubs) appear in the
        // exported SVG, matching what's currently visible in the canvas.
        const structureLayers = structureFiltersToBitmask(structureFilters)
        const baseSvg = await renderSvgViaCore(source, isDark, view, seedColor, presetName, structureLayers)
        if (!baseSvg) return null

        // Splice JBM @font-face data URIs + a force-JBM rule into the renderer's
        // existing <style> block.
        //
        // Why both:
        //   1. SvgRenderer.kt sets `text { font-family: <theme.fontFamily>; }` based
        //      on the theme. For Swiss/Bauhaus that's Inter, which isn't embedded
        //      via @fontsource — it would fall back to system sans on offline viewers.
        //   2. We override with a higher-specificity `text { ... !important }` rule
        //      that forces JBM, alongside the @font-face data URIs that make JBM
        //      actually available offline. The renderer's original rule has no
        //      !important so the override wins.
        //
        // Net effect: SVG always renders in JetBrains Mono regardless of theme,
        // matching the user's expectation that exports = mono.
        const fontCss = await getEmbeddedFontCss()
        let svgWithFonts = baseSvg
        if (fontCss) {
            const overrides = `${fontCss}
text { font-family: 'JetBrains Mono', 'Fira Code', monospace !important; }
`
            svgWithFonts = baseSvg.replace('<style>', `<style>\n${overrides}\n`)
        }

        // Add an XML declaration so it's a proper standalone .svg file.
        const finalSvg = svgWithFonts.startsWith('<?xml')
            ? svgWithFonts
            : '<?xml version="1.0" encoding="UTF-8"?>\n' + svgWithFonts

        return { svg: finalSvg, width: scene.width, height: scene.height }
    }, [source, scene, isDark, theme.name, theme.seedColor, structureFilters, groupBy])

    const triggerDownload = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const handleDownloadSvg = useCallback(async () => {
        if (exporting) return
        setExporting(true)
        setDownloadOpen(false)
        try {
            const rendered = await renderExportSvg()
            if (!rendered) return
            const blob = new Blob([rendered.svg], { type: 'image/svg+xml;charset=utf-8' })
            triggerDownload(blob, exportFilename('svg'))
        } finally { setExporting(false) }
    }, [renderExportSvg, projectName, isDark, exporting])

    const handleDownloadPng = useCallback(async () => {
        if (exporting) return
        setExporting(true)
        setDownloadOpen(false)
        try {
            // Production: server-side PNG via Batik (correct theme, no canvas
            // quirks, no font-loading race). Dev: browser canvas fallback since
            // KMP JS bundle has no Batik rasterizer.
            const isCurated = !SEED_DERIVED_NAMES.has(theme.name)
            const presetName = isCurated ? theme.name : undefined
            const seedColor = isCurated ? undefined : (theme.seedColor ?? undefined)
            const view = groupBy === 'actor' ? 'actors' : groupBy === 'location' ? 'locations' : undefined
            const bitmask = structureFiltersToBitmask(structureFilters)

            const serverBlob = await renderPngViaServer(source, isDark, view, seedColor, presetName, bitmask)
            if (serverBlob) {
                // Server PNG available (prod mode) — direct download.
                triggerDownload(serverBlob, exportFilename('png'))
            } else {
                // DEV FALLBACK — browser canvas rasterization of SVG.
                const rendered = await renderExportSvg()
                if (!rendered) return
                const scale = 2
                const svgBlob = new Blob([rendered.svg], { type: 'image/svg+xml;charset=utf-8' })
                const url = URL.createObjectURL(svgBlob)
                try {
                    const img = new Image()
                    img.crossOrigin = 'anonymous'
                    await new Promise<void>((resolve, reject) => {
                        img.onload = () => resolve()
                        img.onerror = (e) => reject(new Error(`SVG load failed: ${e}`))
                        img.src = url
                    })
                    const w = img.naturalWidth || rendered.width
                    const h = img.naturalHeight || rendered.height
                    const canvas = document.createElement('canvas')
                    canvas.width = Math.ceil(w * scale)
                    canvas.height = Math.ceil(h * scale)
                    const ctx = canvas.getContext('2d')
                    if (!ctx) throw new Error('Canvas 2D context unavailable')
                    ctx.scale(scale, scale)
                    ctx.drawImage(img, 0, 0)
                    await new Promise<void>((resolve, reject) => {
                        canvas.toBlob((blob) => {
                            if (!blob) { reject(new Error('PNG encoding failed')); return }
                            triggerDownload(blob, exportFilename('png'))
                            resolve()
                        }, 'image/png')
                    })
                } finally {
                    URL.revokeObjectURL(url)
                }
            }
        } catch (e) {
            console.error('[Export] PNG failed:', e)
            alert('PNG export failed. Try SVG instead.')
        } finally { setExporting(false) }
    }, [source, isDark, theme.name, theme.seedColor, groupBy, structureFilters, renderExportSvg, exporting])

    // Focus on a node requested from another component (e.g. source cards)
    useEffect(() => {
        if (!pendingFocusNodeId || !scene) return
        // Delay to let tab switch render (visibility: hidden → visible)
        const timeout = setTimeout(() => {
            if (!viewportRef.current) { clearPendingFocus(); return }
            const node = scene.nodes.find(n => n.id === pendingFocusNodeId)
            if (!node) { clearPendingFocus(); return }
            const r = viewportRef.current.getBoundingClientRect()
            if (r.width === 0 || r.height === 0) { clearPendingFocus(); return }
            const currentScale = usePlayground.getState().transform.scale
            const targetScale = Math.max(currentScale, 0.8)
            setTransform({
                x: r.width / 2 - node.x * targetScale,
                y: r.height / 2 - node.y * targetScale,
                scale: targetScale
            })
            clearPendingFocus()
        }, 100)
        return () => clearTimeout(timeout)
    }, [pendingFocusNodeId, pendingFocusSeq, scene])

    // Keyboard shortcuts
    useEffect(() => {
        const fn = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
            if ((e.target as HTMLElement)?.isContentEditable) return
            if ((e.target as HTMLElement)?.closest?.('.cm-editor')) return
            const k = e.key.toLowerCase()
            if (k === '+' || k === '=') { e.preventDefault(); handleZoomIn() }
            if (k === '-' || k === '_') { e.preventDefault(); handleZoomOut() }
            if (k === '0') { e.preventDefault(); handleFit() }
            if (k === 'f') { e.preventDefault(); handleFullscreen() }
            if (k === 'l' || k === 's') { e.preventDefault(); handleFocusSelection() }
            if (k === 'escape') setSettingsOpen(false)
        }
        window.addEventListener('keydown', fn)
        return () => window.removeEventListener('keydown', fn)
    }, [handleZoomIn, handleZoomOut, handleFit, handleFullscreen, handleFocusSelection])

    return (
        <>
            {/* Top toolbar — solid bar, same as other tabs */}
            <div
                className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between h-11 px-2"
                style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border-subtle)' }}
            >
                <div className="flex items-center gap-1">
                    {!isMobile && !leftOpen && <>
                        <button onClick={() => setLeftOpen(true)} className="flex items-center gap-1 h-9 px-2 transition-colors active:scale-[0.97]" style={{ color: 'var(--text-muted)' }}>
                            <div className="w-5 h-5 flex items-center justify-center">{Icons.tabSource}</div>
                            <span className="text-xs font-bold font-mono uppercase tracking-widest">Source</span>
                        </button>
                        <div className="w-px h-5 mx-0.5" style={{ background: 'var(--border-subtle)' }} />
                    </>}
                    <div className="w-8 h-8 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>{Icons.tabDiagram}</div>
                    <span className="text-xs font-bold font-mono uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Diagram</span>
                    {isProcessing && <ProcessingDot />}
                </div>
                <div className="flex items-center gap-1">
                    <ToolbarBtn onClick={() => setDownloadOpen(true)} title="Download" active={downloadOpen}>{Icons.download}</ToolbarBtn>
                    <div data-settings-trigger>
                        <ToolbarBtn onClick={() => setSettingsOpen(o => !o)} title="Settings" active={settingsOpen}>{Icons.settings}</ToolbarBtn>
                    </div>
                    <ToolbarBtn onClick={handleFullscreen} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>{Icons.fullscreen}</ToolbarBtn>
                    {!isMobile && !rightOpen && <>
                        <div className="w-px h-5 mx-0.5" style={{ background: 'var(--border-subtle)' }} />
                        <button onClick={() => setRightOpen(true)} className="flex items-center gap-1 h-9 px-2 transition-colors active:scale-[0.97]" style={{ color: 'var(--text-muted)' }}>
                            <div className="w-5 h-5 flex items-center justify-center">{Icons.tabStructure}</div>
                            <span className="text-xs font-bold font-mono uppercase tracking-widest">Structure</span>
                        </button>
                    </>}
                </div>
            </div>

            {/* Settings panel — anchored below the top toolbar Settings icon. */}
            {settingsOpen && (
                <div data-settings-panel className="absolute top-11 right-0 z-20 pointer-events-auto" onPointerDown={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}>
                    <CanvasSettings
                        open={settingsOpen}
                        onClose={() => setSettingsOpen(false)}
                        filters={structureFilters}
                        onFiltersChange={onFiltersChange}
                    />
                </div>
            )}

            {/* Download sheet — desktop popup / mobile bottom sheet via MobileSheet */}
            <MobileSheet open={downloadOpen} onClose={() => setDownloadOpen(false)} icon={Icons.download} title="Download">
                <div className="flex flex-col">
                    <button onClick={handleDownloadSvg} disabled={exporting}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all hover:bg-white/5 disabled:opacity-50"
                        style={{ color: 'var(--text)' }}>
                        <span className="text-xs font-bold font-mono tracking-tight">{exporting ? '…' : 'svg'}</span>
                    </button>
                    <button onClick={handleDownloadPng} disabled={exporting}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all hover:bg-white/5 disabled:opacity-50"
                        style={{ color: 'var(--text)' }}>
                        <span className="text-xs font-bold font-mono tracking-tight">{exporting ? '…' : 'png'}</span>
                    </button>
                </div>
            </MobileSheet>

            {/* Bottom-left floating — zoom + recentre */}
            <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1">
                <button onClick={handleFit} className="text-xs font-mono font-bold transition-colors active:scale-[0.97]" style={{ color: 'var(--text-muted)' }}>
                    {zoomPercent}%
                </button>
                <ToolbarBtn onClick={handleFit} title="Recentre">{Icons.recentre}</ToolbarBtn>
                {focusedNodeId && <ToolbarBtn onClick={handleFocusSelection} title="Focus selection" accent>{Icons.focus}</ToolbarBtn>}
            </div>

            {/* Top-right — theme controls: mode + theme name. */}
            <CanvasOverlay position="top-right" triggerAttr="theme" rows={[
                {
                    icon: isDark ? Icons.darkMode : Icons.lightMode,
                    iconColor: 'var(--accent)',
                    label: 'mode',
                    value: modeValueLabel,
                    valueColor: 'var(--accent)',
                    onClick: toggleMode,
                    title: isDark ? 'Switch to light' : 'Switch to dark',
                },
                {
                    icon: Icons.theme,
                    iconColor: 'var(--accent)',
                    label: 'theme',
                    value: themeNameLabel,
                    valueColor: themeOpen ? 'var(--accent-subtle)' : 'var(--accent)',
                    onClick: () => setThemeOpen(o => !o),
                    title: 'Theme',
                },
            ]} />


            {/* Dedicated theme sheet — anchored below the top-right cluster. */}
            {themeOpen && (
                <div data-theme-panel className="absolute top-24 right-3 z-20 pointer-events-auto" onPointerDown={e => e.stopPropagation()} onWheel={e => e.stopPropagation()}>
                    <ThemeSheet open={themeOpen} onClose={() => setThemeOpen(false)} />
                </div>
            )}
        </>
    )
}

export { FILTER_DEFAULTS }
export type { StructureFilters }
